'use client';

import * as React from 'react';
import { DeviceSettingsModal } from '@/components/DeviceSettingsModal';
import { useContext } from 'react';
import { PollContext } from './PollContext';
import { StreamContext } from './StreamContext';
import { BreakoutContext } from './BreakoutContext';
import { BreakoutControlPanel } from './BreakoutComponents';
import { ControlBarAnchoredDropdown } from './control-bar-anchored-menu';

type MoreOptionsButtonProps = {
  inline?: boolean;
  onSettingsClick?: () => void;
  onHelpClick?: () => void;
  onFeedbackClick?: () => void;
};

/**
 * More options button with dropdown menu. If `inline` is true, it renders as a plain button to be inserted inside a toolbar.
 */
export function MoreOptionsButton({
  inline = false,
  onSettingsClick,
  onHelpClick,
  onFeedbackClick,
}: MoreOptionsButtonProps) {
  const [leftPx, setLeftPx] = React.useState<number | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = React.useState(false);
  const [isPipActive, setIsPipActive] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const pipVideoRef = React.useRef<HTMLVideoElement | null>(null);

  // Hooks para os contextos (opcionais - podem não estar disponíveis)
  const pollContext = useContext(PollContext);
  const streamContext = useContext(StreamContext);
  const breakoutContext = useContext(BreakoutContext);
  
  const [isBreakoutModalOpen, setIsBreakoutModalOpen] = React.useState(false);

  // Position the button to the right of the control bar when not inline
  React.useEffect(() => {
    if (inline) return;
    if (typeof window === 'undefined') return;
    const container = document.querySelector('.lk-room-container') as HTMLElement | null;
    const bar = document.querySelector('.lk-control-bar') as HTMLElement | null;
    if (!container || !bar) return;

    const update = () => {
      const cr = container.getBoundingClientRect();
      const br = bar.getBoundingClientRect();
      const left = br.left + br.width + 16 - cr.left; // 16px gap to the bar
      setLeftPx(left);
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(container);
    ro.observe(bar);
    window.addEventListener('resize', update);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [inline]);

  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (wrapperRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }

      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const handleMenuClick = (callback?: () => void) => {
    callback?.();
    setIsOpen(false);
  };

  const handleSettingsClick = () => {
    setIsSettingsModalOpen(true);
    setIsOpen(false); // Fechar o dropdown
  };

  // Lógica de Picture-in-Picture
  React.useEffect(() => {
    const onEnterPip = () => {
      setIsPipActive(true);
    };
    const onLeavePip = () => {
      setIsPipActive(false);
      pipVideoRef.current = null;
    };
    
    // Verifica se já está em PiP ao montar
    if (document.pictureInPictureElement) {
      setIsPipActive(true);
      pipVideoRef.current = document.pictureInPictureElement as HTMLVideoElement;
    }
    
    document.addEventListener('enterpictureinpicture', onEnterPip, true);
    document.addEventListener('leavepictureinpicture', onLeavePip, true);
    return () => {
      document.removeEventListener('enterpictureinpicture', onEnterPip, true);
      document.removeEventListener('leavepictureinpicture', onLeavePip, true);
    };
  }, []);

  const togglePip = async () => {
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPipActive(false);
        return;
      }

      let videoToPip = document.querySelector('video[data-lk-source="screen_share"]') as HTMLVideoElement;
      if (!videoToPip) {
        const focusLayout = document.querySelector('.lk-focus-layout video');
        if (focusLayout) videoToPip = focusLayout as HTMLVideoElement;
      }
      if (!videoToPip) {
        const allVideos = Array.from(document.querySelectorAll('video'));
        videoToPip = allVideos.find(v => v.srcObject && v.readyState >= 2) as HTMLVideoElement;
      }

      if (videoToPip) {
        await videoToPip.requestPictureInPicture();
        pipVideoRef.current = videoToPip;
        setIsPipActive(true);
      } else {
        alert("Nenhum vídeo ativo encontrado para destacar.");
      }
    } catch (error) {
      console.error("Erro ao ativar PiP:", error);
    }
  };

  const handlePollClick = () => {
    if (pollContext) {
      pollContext.togglePollModal();
    }
    setIsOpen(false);
  };

  const handleStreamClick = () => {
    if (streamContext) {
      streamContext.toggleStreamModal();
    }
    setIsOpen(false);
  };

  const handleBreakoutClick = () => {
    setIsBreakoutModalOpen(true);
    setIsOpen(false);
  };

  const menuItemStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    background: 'transparent',
    border: 'none',
    color: 'var(--lk-fg, #ffffff)',
    textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    transition: 'background-color 0.2s ease',
  };

  const DropdownMenu = (
    <ControlBarAnchoredDropdown
      isOpen={isOpen}
      anchorRef={buttonRef}
      menuRef={dropdownRef}
      className="more-options-dropdown em-more-options-dropdown"
      style={{
        background: 'var(--lk-bg, #1a1a1a)',
        border: '1px solid var(--lk-border, rgba(255,255,255,0.1))',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        minWidth: 200,
        overflow: 'hidden',
      }}
    >
      <div onMouseDown={(e) => e.preventDefault()}>
      {/* Botão de Picture-in-Picture */}
      <button
        className="more-options-dropdown__item em-more-options-dropdown__item"
        onClick={togglePip}
        style={menuItemStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--lk-bg2, rgba(255,255,255,0.05))';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
      >
        {isPipActive ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
            <line x1="7" y1="2" x2="13" y2="2"></line>
            <line x1="7" y1="22" x2="13" y2="22"></line>
            <line x1="2" y1="12" x2="22" y2="12"></line>
            <rect x="13" y="13" width="7" height="5" rx="1" fill="currentColor" fillOpacity="0.2"></rect>
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
            <path d="M12 17h8v-6" />
            <path d="M22 2l-2.5 2.5" />
            <rect x="13" y="13" width="7" height="5" rx="1"></rect>
          </svg>
        )}
        {isPipActive ? 'Sair do Mini Player' : 'Mini Player (Picture-in-Picture)'}
      </button>

      {/* Botão de Enquete (só para host) */}
      {pollContext && pollContext.isHost && (
        <button
          className="more-options-dropdown__item em-more-options-dropdown__item"
          onClick={handlePollClick}
          style={menuItemStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--lk-bg2, rgba(255,255,255,0.05))';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20V10" />
            <path d="M18 20V4" />
            <path d="M6 20v-4" />
          </svg>
          Criar Nova Enquete
        </button>
      )}

      {/* Botão de Stream (só para host) */}
      {streamContext && streamContext.isHost && (
        <button
          className="more-options-dropdown__item em-more-options-dropdown__item"
          onClick={handleStreamClick}
          style={{
            ...menuItemStyle,
            color: (streamContext.activeStreams.youtube || streamContext.activeStreams.instagram) 
              ? 'var(--lk-red, #ef4444)' 
              : 'var(--lk-fg, #ffffff)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--lk-bg2, rgba(255,255,255,0.05))';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={(streamContext.activeStreams.youtube || streamContext.activeStreams.instagram) ? 'animate-pulse' : ''}>
            <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" />
            <path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" />
            <circle cx="12" cy="12" r="2" />
            <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" />
            <path d="M19.1 4.9C23 8.8 23 15.1 19.1 19" />
          </svg>
          Transmissão (Youtube/Instagram)
        </button>
      )}

      {/* Botão de Salas de Apoio (só para host) */}
      {breakoutContext && breakoutContext.isHost && !breakoutContext.isInBreakout && (
        <button
          className="more-options-dropdown__item em-more-options-dropdown__item"
          onClick={handleBreakoutClick}
          style={menuItemStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--lk-bg2, rgba(255,255,255,0.05))';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1"></rect>
            <rect x="14" y="3" width="7" height="7" rx="1"></rect>
            <rect x="3" y="14" width="7" height="7" rx="1"></rect>
            <rect x="14" y="14" width="7" height="7" rx="1"></rect>
          </svg>
          Salas de Apoio
        </button>
      )}

      {/* Separador */}
      <div style={{ height: 1, background: 'var(--lk-border, rgba(255,255,255,0.1))', margin: '4px 0' }} />

      {/* Botão de Configurações */}
      <button
        className="more-options-dropdown__item em-more-options-dropdown__item"
        onClick={handleSettingsClick}
        style={menuItemStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--lk-bg2, rgba(255,255,255,0.05))';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.2573 9.77251 19.9887C9.5799 19.7201 9.31074 19.5166 9 19.41C8.69838 19.2769 8.36381 19.2372 8.03941 19.296C7.71502 19.3548 7.41568 19.5095 7.18 19.74L7.12 19.8C6.93425 19.986 6.71368 20.1335 6.47088 20.2341C6.22808 20.3348 5.96783 20.3866 5.705 20.3866C5.44217 20.3866 5.18192 20.3348 4.93912 20.2341C4.69632 20.1335 4.47575 19.986 4.29 19.8C4.10405 19.6143 3.95653 19.3937 3.85588 19.1509C3.75523 18.9081 3.70343 18.6478 3.70343 18.385C3.70343 18.1222 3.75523 17.8619 3.85588 17.6191C3.95653 17.3763 4.10405 17.1557 4.29 16.97L4.35 16.91C4.58054 16.6743 4.73519 16.375 4.794 16.0506C4.85282 15.7262 4.81312 15.3916 4.68 15.09C4.55324 14.7942 4.34276 14.542 4.07447 14.3643C3.80618 14.1866 3.49179 14.0913 3.17 14.09H3C2.46957 14.09 1.96086 13.8793 1.58579 13.5042C1.21071 13.1291 1 12.6204 1 12.09C1 11.5596 1.21071 11.0509 1.58579 10.6758C1.96086 10.3007 2.46957 10.09 3 10.09H3.09C3.42099 10.0823 3.742 9.97512 4.01062 9.78251C4.27925 9.5899 4.48266 9.32074 4.59 9.01C4.72312 8.70838 4.76282 8.37381 4.704 8.04941C4.64519 7.72502 4.49054 7.42568 4.26 7.19L4.2 7.13C4.01405 6.94425 3.86653 6.72368 3.76588 6.48088C3.66523 6.23808 3.61343 5.97783 3.61343 5.715C3.61343 5.45217 3.66523 5.19192 3.76588 4.94912C3.86653 4.70632 4.01405 4.48575 4.2 4.3C4.38575 4.11405 4.60632 3.96653 4.84912 3.86588C5.09192 3.76523 5.35217 3.71343 5.615 3.71343C5.87783 3.71343 6.13808 3.76523 6.38088 3.86588C6.62368 3.96653 6.84425 4.11405 7.03 4.3L7.09 4.36C7.32568 4.59054 7.62502 4.74519 7.94941 4.804C8.27381 4.86282 8.60838 4.82312 8.91 4.69H9C9.29577 4.56324 9.54802 4.35276 9.72569 4.08447C9.90337 3.81618 9.99872 3.50179 10 3.18V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Configurações
      </button>
      </div>
    </ControlBarAnchoredDropdown>
  );

  const Btn = (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        data-more-options-button="true"
        onClick={handleClick}
        aria-label="Mais opções"
        aria-expanded={isOpen}
        style={{
          width: 44,
          height: 44,
          padding: 0,
          borderRadius: 8,
          background: isOpen ? 'var(--lk-bg3, rgba(255,255,255,0.1))' : 'transparent',
          color: 'var(--lk-fg)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.background = 'var(--lk-bg3, rgba(255,255,255,0.1))';
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.background = 'transparent';
          }
        }}
      >
        <svg
          aria-hidden="true"
          height="24"
          width="24"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            fill="white"
            d="M16.5 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0Zm1.5 0a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0ZM9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0Zm1.5 0a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0ZM4.5 15a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm0-1.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z"
          />
        </svg>
      </button>
      {DropdownMenu}
    </div>
  );

  if (inline) {
    return (
      <>
        {Btn}
        <DeviceSettingsModal
          open={isSettingsModalOpen}
          onOpenChange={setIsSettingsModalOpen}
          defaultTab="audio"
        />
        {breakoutContext && (
          <BreakoutControlPanel
            isOpen={isBreakoutModalOpen}
            onOpenChange={setIsBreakoutModalOpen}
          />
        )}
      </>
    );
  }

  return (
    <div
      className="more-options-btn-overlay"
      ref={containerRef}
      style={{ left: leftPx ?? undefined }}
    >
      {Btn}
      <DeviceSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
        defaultTab="audio"
      />
      {breakoutContext && (
        <BreakoutControlPanel
          isOpen={isBreakoutModalOpen}
          onOpenChange={setIsBreakoutModalOpen}
        />
      )}
    </div>
  );
}
